import {
  type CallWindowFilter,
  type CallWindowJobCapFilter,
  type CallWindowLimitDateFilter,
  type CallWindowLimitFilter,
  type CreateAiReportReq,
  type Event,
  type FormSubmission,
  type GeneratedTask,
  type GeneratedTaskDocument,
  type GetRequest,
  type LoadInternalResource,
  type MembershipSalesFilter,
  type PaginationRequest,
  type ProposalIntakeForm,
  type ProposalIntakeSystem,
  type StandardMetricsFilter,
  type TokenProposal,
  type User,
} from '@kalos/kalos-rpc';

import { type DeletedEventsFilterForm } from '../../modules/ComponentsLibrary/DeletedServiceCallsReport';
import { type EquipmentDiagnosisHistoryQueryFilter } from '../../modules/ComponentsLibrary/Equipment/equipmentDiagnosisHistory';
import { type CallHistoryFilter } from '../../modules/Softphone/hooks/useGetCallHistoryQuery';
import { type InternalDocumentClientService, type PerDiemClientService } from '../../tools/helpers';
import { type EventDetailsFilters, type JobAdminFilter } from './eventDetails';
import { type ActivityLogFilter } from './useActivityLogQuery';
import { type AiChatPromptFilter } from './useAiChatPromptQuery';
import { type AiResponseFilter } from './useAiResponseQuery';
import { type ApiKeyFilter } from './useApiKeyClientServiceQuery';
import { type AssetInstanceFilter } from './useAssetInstanceQuery';
import { type ClassCodeFilters } from './useClassCodeQuery';
import { type ContractFrequencyFilter } from './useContractFrequencyQuery';
import { type ContractFilter } from './useContractsQuery';
import { type CreditCardFilter } from './useCreditCardsQuery';
import { type DiagnosisFilter, type DiagnosisStatusFilter } from './useDiagnosisQuery';
import { type DispatchableTechFilter, type DispatchCallFilter } from './useDispatchQuery';
import { type DocumentFilter } from './useDocumentsQuery';
import { type EmployeeFunctionFilter } from './useEmployeeFunction';
import {
  type EmployeeItemCategoryFilter,
  type EmployeeItemFilter,
  type EmployeeItemOptionFilter,
} from './useEmployeeItemsQuery';
import { type EventFeedFilters } from './useEventFeedQuery';
import { type EventPhotoFilter } from './useEventPhotoQuery';
import { type EventQuotesFilter } from './useEventQuotes';
import {
  type CallCountRequestFilter,
  type EventFilter,
  type MostLikelyJobRequestFilter,
} from './useEventsQuery';
import { type FileTagsFilters } from './useFileTagsQuery';
import { type GroupFilter } from './useGroupsQuery';
import { type InternalDocumentFilter } from './useInternalDocumentsQuery';
import { type InternalLinkFilter } from './useInternalLinkQuery';
import { type InvoiceFilter } from './useInvoiceQuery';
import { type JobSubTypesFilter } from './useJobSubTypesQuery';
import { type LodgingRequestAssignmentFilter, type LodgingRequestFilter } from './useLodging';
import {
  type MeasureQuickDispatchesFilter,
  type MeasureQuickReadingsFilter,
} from './useMeasureQuickQuery';
import { type MemoCommentFilter, type MemoCommentLogFilter, type MemoFilter } from './useMemoQuery';
import { type NewHireFilter, type NewHireOptionFilter } from './useNewHireQuery';
import { type OCRFilter } from './useOCR';
import { type PendingBillingFilter } from './usePendingBillingQuery';
import { type PerDiemFilter } from './usePerDiemQuery';
import { type PerformanceReviewFilter } from './usePerformanceReviewQuery';
import {
  type InspectionFilter,
  type PermitFilter,
  type PermitGroupFilter,
  type PermitStatusFilter,
  type PermitTradeFilter,
} from './usePermits';
import { type ProjectEventFilter } from './useProjectEventsQuery';
import { type PropertyFilter } from './usePropertiesQuery';
import type { QuoteDocumentFilters } from './useQuoteDocumentQuery';
import { type QuoteLineFilters } from './useQuoteLineQuery';
import { type QuotePhotoFilters } from './useQuotePhotoQuery';
import { type MaintenanceFilter, type ReadingFilter } from './useReadingQuery';
import {
  type ReimbursementDocumentFilter,
  type ReimbursementFilter,
} from './useReimbursementQuery';
import {
  type EmployeeKPIQueryFilter,
  type ReportFilter,
  type TimeOffMetricRequestFilter,
} from './useReportQuery';
import type { SellSheetFilters } from './useSellSheetBatchGetQuery';
import {
  type RefrigerantTypeFilter,
  type ServiceFilter,
  type SystemStatusFilter,
} from './useServiceItemQuery';
import { type ServicesRenderedFilter } from './useServicesRenderedQuery';
import {
  type SpiffOptionFilter,
  type SpiffTypeFilter,
  type TaskFilter,
} from './useTaskClientServiceQuery';
import { type TerminationFormFilter } from './useTerminationFormQuery';
import { type TimeOffFilter } from './useTimeOffQuery';
import { type TimesheetDepartmentFilters } from './useTimesheetDepartmentQuery';
import { type TimeSheetFilters, type TimesheetLineFilter } from './useTimesheetQuery';
import { type CatalogItemCategoryFilter } from './useToolFundCatalogItemCategoryQuery';
import { type CatalogItemFilter } from './useToolFundCatalogQuery';
import { type RequisitionFilter } from './useToolFundQuery';
import { type ToolFundSettingsFilter } from './useToolFundSettingsQuery';
import { type ToolFundTransactionFilter } from './useToolFundTransaction';
import { type ToolTradeMappingFilter } from './useToolTradeMappingQuery';
import { type TradeTypeFilter } from './useTradeTypeQuery';
import { type TransactionAccountFilters } from './useTransactionAccountsQuery';
import { type TransactionActivityFilter } from './useTransactionActivityQuery';
import { type applyFilters } from './useTransactionAdminQuery';
import { type TransactionDocumentFilter } from './useTransactionDocumentQuery';
import { type TransactionLineItemFilter } from './useTransactionLineItem';
import { type TransactionQueryFilters } from './useTransactionQuery';
import { type TransactionStatusFilters } from './useTransactionsStatusQuery';
import { type useTripDistanceQueryArgs } from './useTripDistanceQuery';
import { type TripFilter } from './useTripts';
import { type UserContactFilters } from './useUserContactQuery';
import { type UserGroupLinkFilter } from './useUserGroupLinksQuery';
import {
  type BatchGetPermissionGroupUserFilter,
  type UserAttributesFilter,
  type UserFilters,
} from './useUserQuery';
import {
  type VehicleAssetFilter,
  type VehicleFilter,
  type VehicleLogFilter,
  type VehiclePhotoFilter,
  type VehicleTrailerFilter,
} from './useVehicles';
import { type VendorQueryFilters } from './useVendorQuery';
import { type EntityFilter } from './utils';
import { sortObjectKeys, trimEmptyValues } from './utils';

export type FormSubmissionFilter = EntityFilter<FormSubmission>;
export type GeneratedTaskFilter = EntityFilter<GeneratedTask>;
export type GeneratedTaskDocumentFilter = EntityFilter<GeneratedTaskDocument>;

export const queryKeys = {
  analytics: {
    root: 'analytics',
    listPricingSellSheets: 'analytics-listPricingSellSheets',
    getListPricingSellSheetsHash: (payload: {
      filter?: StandardMetricsFilter;
      pagination?: PaginationRequest;
    }) => JSON.stringify(sortObjectKeys(payload as Record<string, unknown>)),
    listCallbackEvents: 'analytics-listCallbackEvents',
    getListCallbackEventsHash: (payload: {
      filter?: StandardMetricsFilter;
      pagination?: PaginationRequest;
    }) => JSON.stringify(sortObjectKeys(payload as Record<string, unknown>)),
    membershipSalesMetrics: 'analytics-membershipSalesMetrics',
    listMembershipSales: 'analytics-listMembershipSales',
    getMembershipSalesHash: (payload: {
      filter?: MembershipSalesFilter;
      pagination?: PaginationRequest;
    }) => JSON.stringify(sortObjectKeys(payload as Record<string, unknown>)),
    /** Employee dashboard self-service field tech metrics (`getTechnicianPerformanceDetail`). */
    employeeDashboardTechnicianPerformanceDetail:
      'analytics-employee-dashboard-technician-performance-detail',
    getEmployeeDashboardTechnicianPerformanceDetailHash: (params: {
      technicianId: number;
      startDate: string;
      endDate: string;
    }) => JSON.stringify(sortObjectKeys(params as Record<string, unknown>)),
  },
  softphone: {
    root: 'softphone',
    me: 'softphone-me',
    callHistory: 'softphone-call-history',
    getCallHistoryHash: (filter: CallHistoryFilter) => JSON.stringify(sortObjectKeys(filter)),
    currentCallQueue: 'softphone-current-call-queue',
  },
  lodgingStatuses: {
    root: 'lodgingStatuses',
    all: 'lodgingStatuses-all',
  },
  s3: {
    root: 's3',
    file: 's3-file',
    multipartUpload: 's3-multipart-upload',
    multipartUploadStart: 's3-multipart-upload-start',
    multipartUploadUrl: 's3-multipart-upload-url',
    multipartUploadComplete: 's3-multipart-upload-complete',
    downloadZip: {
      root: 's3-download-zip',
      getHash: (fileIds: number[]) => JSON.stringify(fileIds.sort()),
    },
    largeFileUpload: 's3-large-file-upload',
    getLargeFileUploadHash: (params: { bucket: string; key: string; fileSize: number }) =>
      JSON.stringify(sortObjectKeys(params)),
  },
  lodgingOverlap: {
    root: 'lodgingOverlap',
  },
  lodgingRequests: {
    root: 'lodgingRequests',
    list: 'lodgingRequestsList',
    getHash: (filter: LodgingRequestFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  lodgingChangeRequests: {
    root: 'lodgingChangeRequests',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  lodgingAssignments: {
    root: 'lodgingAssignments',
    list: 'lodgingAssignmentsList',
    getHash: (filter: LodgingRequestAssignmentFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  permitGroups: {
    root: 'permitGroups',
    list: 'permitGroupsList',
    getHash: (filter: PermitGroupFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  permits: {
    root: 'permits',
    list: 'permitsList',
    getHash: (filter: PermitFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  permitStatuses: {
    root: 'permitStatuses',
    list: 'permitStatusesList',
    getHash: (filter: PermitStatusFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  permitTrade: {
    root: 'permitTrade',
    list: 'permitTradeList',
    getHash: (filter: PermitTradeFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  inspections: {
    root: 'inspections',
    list: 'inspectionsList',
    getHash: (filter: InspectionFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  inspectionStatus: {
    root: 'inspectionStatus',
    list: 'inspectionStatusList',
  },
  properties: {
    root: 'properties',
    list: 'propertiesList',
    search: 'propertiesSearch',
    getHash: (filter: PropertyFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  userProperties: {
    root: 'userProperties',
    search: 'userPropertiesSearch',
  },
  userContact: {
    root: 'userContact',
    list: 'userContactList',
    getHash: (filters: UserContactFilters) => JSON.stringify(sortObjectKeys(filters)),
  },
  customerPriorities: {
    root: 'customerPriorities',
  },
  scMappings: {
    root: 'scMappings',
    subscribers: 'scMappingSubscribers',
    unmappedTrades: 'scMappingUnmappedTrades',
    technicians: 'scMappingTechnicians',
  },
  scPhoneCheckIn: {
    root: 'scPhoneCheckIn',
  },
  scCertification: {
    root: 'scCertification',
    certifiedTechnicians: 'scCertifiedTechnicians',
  },
  scCustomerSettings: {
    root: 'scCustomerSettings',
  },
  scInbound: {
    root: 'scInbound',
  },
  featureFlags: {
    root: 'featureFlags',
    changes: 'featureFlagChanges',
  },
  addressSuggestions: {
    root: 'addressSuggestions',
  },
  tripDistance: {
    root: 'tripDistance',
    getTripDistanceHash: (request: useTripDistanceQueryArgs) =>
      `${request.originAddress}-${request.destinationAddress}`,
  },
  employeeItems: {
    root: 'employeeItems',
    list: 'employeeItemsList',
    getHash: (filter: EmployeeItemFilter) => JSON.stringify(sortObjectKeys(filter)),
    employeeItemsOptions: 'employeeItemsOptions',
    employeeItemsOptionsList: 'employeeItemsOptionsList',
    getEmployeeItemOptionsHash: (filter: EmployeeItemOptionFilter) =>
      JSON.stringify(sortObjectKeys(filter)),
  },
  employeeItemCategories: {
    root: 'employeeItemCategories',
    list: 'employeeItemCategoriesList',
    getHash: (filter: EmployeeItemCategoryFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  employeeFunctions: {
    root: 'employeeFunctions',
    list: 'employeeFunctionsList',
    getHasH: (filter: EmployeeFunctionFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  referralSources: {
    root: 'referralSources',
    list: 'referralSourcesList',
  },
  contactPreferences: {
    root: 'contactPreferences',
    list: 'contactPreferencesList',
  },
  eventTrades: {
    root: 'eventTrades',
    list: 'eventTradesList',
  },
  eventTradeResolution: {
    root: 'eventTradeResolution',
  },
  subtypeTrades: {
    root: 'subtypeTrades',
  },
  /** BatchGetEquipmentDiagnosisHistory — Systems tab equipment history */
  equipmentDiagnosisHistory: {
    root: 'equipmentDiagnosisHistory',
    getHash: (filter: EquipmentDiagnosisHistoryQueryFilter) =>
      JSON.stringify(sortObjectKeys(filter)),
  },
  eventDetails: {
    root: 'eventDetails',
    jobAdmins: 'jobAdmins',
    jobAdminsList: 'jobAdminsList',
    getJobAdminHash: (filter: JobAdminFilter) => JSON.stringify(sortObjectKeys(filter)),
    getEventDetailsHash: (filters: EventDetailsFilters) => {
      return JSON.stringify(sortObjectKeys(filters));
    },
  },
  jobHits: {
    root: 'jobHits',
    hash: (filter: MostLikelyJobRequestFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  events: {
    root: 'events',
    getEventHash: (filter: EventFilter) => JSON.stringify(sortObjectKeys({ ...filter })),
    list: 'eventsList',
    deletedEvents: 'deletedEvents',
    searchUserByPhone: 'searchUserByPhone',
    getUserByPhoneHash: (phoneNumber: string) => `phone-${phoneNumber}`,
    validateJobNumber: 'validateJobNumber',
    getValidateJobNumberHash: (jobNumber: number, departmentId?: number) =>
      `job-${jobNumber}-dept-${departmentId ?? 'none'}`,
    getDeletedEventsHash: ({ filter, page }: { filter: DeletedEventsFilterForm; page: number }) =>
      `${JSON.stringify(sortObjectKeys(filter))}-${page}`,
    getEventsListHash: (request: Event) => {
      const reqProperties = trimEmptyValues(request);
      return JSON.stringify(sortObjectKeys(reqProperties));
    },
    getEventsBatchGetHash: (request: EventFilter) => {
      return JSON.stringify(sortObjectKeys(request));
    },
    /** Employee dashboard: callback jobs where `callback_user_ids` contains the tech (BatchGet). */
    employeeDashboardCallbackAssignments: 'events-employee-dashboard-callback-assignments',
    getEmployeeDashboardCallbackAssignmentsHash: (params: {
      userId: number;
      startDate: string;
      endDate: string;
      pageNumber: number;
    }) => JSON.stringify(sortObjectKeys(params as Record<string, unknown>)),
    getEventFiltersHash: (filters: EventFilter) => {
      return JSON.stringify(sortObjectKeys(filters));
    },
    details: 'eventDetails',
    callWindow: {
      root: 'callWindow',
      getCallWindowHash: (filter: CallCountRequestFilter) => JSON.stringify(sortObjectKeys(filter)),
    },
  },
  schedule: {
    root: 'schedule',
    board: 'scheduleBoard',
    shifts: 'scheduleShifts',
    timeoff: 'scheduleTimeoffWindows',
    weekExceptions: 'scheduleWeekExceptions',
    lookups: 'scheduleLookups',
    travel: 'scheduleTravel',
    travelDays: 'scheduleTravelDays',
    travelTiers: 'scheduleTravelTiers',
    jobHours: 'jobHours',
    emailPreview: 'emailPreview',
    lookahead: 'lookahead',
    slotHistory: 'slotHistory',
    myDays: 'myDays',
    crewDays: 'crewDays',
    conflicts: 'scheduleConflicts',
    tradeRequests: 'scheduleTradeRequests',
    teams: 'scheduleTeams',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  timeClockEntry: {
    root: 'timeClockEntry',
    active: 'activeTimeClockEntry',
  },
  eventAssignment: {
    root: 'eventAssignment',
    employeeDashboardNextAssignments: 'event-assignment-employee-dashboard-next-assignments',
  },
  eventFeed: {
    root: 'eventFeed',
    list: 'eventFeedList',
    /** `sortObjectKeys` drops numeric 0, which breaks pagination cache keys for page 0. */
    getHash: (filters: EventFeedFilters) =>
      JSON.stringify({
        ...sortObjectKeys(filters as Record<string, unknown>),
        pageNumber: filters.pageNumber ?? 0,
      }),
  },
  eventQuotes: {
    root: 'eventQuotes',
    list: 'eventQuotesList',
    getHash: (filter: EventQuotesFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  invoice: {
    root: 'invoice',
    list: 'invoiceList',
    getHash: (filter: InvoiceFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  helcimCardAudit: {
    root: 'helcimCardAudit',
  },
  helcimCardLink: {
    root: 'helcimCardLink',
  },
  helcimCardsOnFile: {
    root: 'helcimCardsOnFile',
  },
  helcimCardReaders: {
    root: 'helcimCardReaders',
  },
  helcimEvent: {
    root: 'helcimEvent',
    byId: 'helcimEventById',
  },
  /** Cash/check/card `Payment` rows for a service call (GetPaymentsByEventID). */
  eventPayments: {
    root: 'eventPayments',
    byEventId: 'eventPaymentsByEventId',
    getEventIdHash: (eventId: number) => String(eventId),
  },
  memoList: {
    root: 'memoList',
    getMemoListHash: (filters: MemoFilter) => JSON.stringify(sortObjectKeys({ ...filters })),
  },
  memo: {
    root: 'memo',
    getMemoHash: (filters: MemoFilter) => JSON.stringify(sortObjectKeys({ ...filters })),
  },
  memoReport: {
    root: 'memoReport',
    getMemoReportHash: (req: CreateAiReportReq) => JSON.stringify(sortObjectKeys({ ...req })),
  },
  memoCommentList: {
    root: 'memoCommentList',
    getMemoCommentListHash: (filters: MemoCommentFilter) =>
      JSON.stringify(sortObjectKeys({ ...filters })),
  },
  memoCommentLogList: {
    root: 'memoCommentLogList',
    getMemoCommentLogListHash: (filters: MemoCommentLogFilter) =>
      JSON.stringify(sortObjectKeys({ ...filters })),
  },
  jobMemoSummary: {
    root: 'jobMemoSummary',
  },
  jobSummary: {
    root: 'jobSummary',
  },
  memoStatusList: {
    root: 'memoStatusList',
    getMemoStatusListHash: (filters: MemoFilter) => JSON.stringify(sortObjectKeys({ ...filters })),
  },
  memoClassificationList: {
    root: 'memoClassificationList',
    getMemoClassificationListHash: (filters: MemoFilter) =>
      JSON.stringify(sortObjectKeys({ ...filters })),
  },
  noteTranslation: {
    root: 'noteTranslation',
    getTextNoteHash: (params: { memoId: number; targetLanguage?: string }) =>
      `textNote-${JSON.stringify(sortObjectKeys({ memoId: params.memoId, targetLanguage: params.targetLanguage ?? 'en' }))}`,
    getByFileIdHash: (params: { fileId: number; targetLanguage?: string }) =>
      `byFileId-${JSON.stringify(sortObjectKeys({ fileId: params.fileId, targetLanguage: params.targetLanguage ?? 'en' }))}`,
  },
  reminder: {
    root: 'reminder',
  },
  windowBlock: {
    root: 'windowBlock',
    list: 'windowBlockList',
  },
  usersManager: {
    root: 'usersManager',
    getUserManagerHash: (req: UserFilters) => JSON.stringify(sortObjectKeys({ ...req })),
  },
  user: {
    root: 'user',
    list: 'userList',
    byPermissionGroup: 'userByPermissionGroup',
    byPhoneNumber: 'userByPhoneNumber',
    getHash: (filters: UserFilters) => JSON.stringify(sortObjectKeys(filters)),
    getUserByPhoneNumberHash: (phoneNumber: string) => `phone-${phoneNumber}`,
    userAttributes: 'userAttributes',
    getUserAttributesHash: (filters: UserAttributesFilter) =>
      JSON.stringify(sortObjectKeys(filters)),
    addressChangeRequest: 'addressChangeRequest',
    permissionGroupUser: {
      root: 'permissionGroupUser',
      list: 'permissionGroupUserList',
      getHash: (filters: BatchGetPermissionGroupUserFilter) =>
        JSON.stringify(sortObjectKeys(filters)),
    },
  },
  userGroupLinks: {
    root: 'userGroupLinks',
    list: 'userGroupLinksList',
    getHash: (filters: UserGroupLinkFilter) => JSON.stringify(sortObjectKeys(filters)),
  },
  group: {
    root: 'group',
    list: 'groupList',
    getHash: (filters: GroupFilter) => JSON.stringify(sortObjectKeys(filters)),
  },
  userBatchGet: {
    root: 'userBatchGet',
    getUserBatchGetHash: (filters: UserFilters) => JSON.stringify(sortObjectKeys(filters)),
  },
  technicians: {
    root: 'technicians',
  },
  contracts: {
    root: 'contracts',
    list: 'contractsList',
    getHash: (filter: ContractFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  commercialServiceQueue: {
    root: 'commercialServiceQueue',
    rows: 'commercialServiceQueueRows',
    locations: 'commercialServiceQueueLocations',
    tabCounts: 'commercialServiceQueueTabCounts',
    savedViews: 'commercialServiceQueueSavedViews',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  memberships: {
    root: 'memberships',
    list: 'membershipsList',
    types: 'membershipTypes',
    typeLines: 'membershipTypeLines',
    instanceLines: 'membershipInstanceLines',
    channels: 'membershipChannels',
    pmSchedule: 'membershipsPmSchedule',
    filterOrders: 'membershipsFilterOrders',
    statuses: 'membershipStatuses',
    dashboard: 'membershipsDashboard',
    dashboardSubRows: 'membershipsDashboardSubRows',
    dashboardTabCounts: 'membershipsDashboardTabCounts',
    dashboardCustomers: 'membershipsDashboardCustomers',
    amendments: 'membershipsAmendments',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  contractsFrequency: {
    root: 'contractsFrequency',
    list: 'contractsFrequencyList',
    getHash: (filter: ContractFrequencyFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  jobSubTypes: {
    root: 'jobSubTypes',
    getJobSubTypesHash: (filters: JobSubTypesFilter) => JSON.stringify(sortObjectKeys(filters)),
  },
  jobTypes: {
    root: 'jobTypes',
  },
  pto: {
    root: 'pto',
    inquiry: 'inquiry',
    list: 'list',
  },
  pendingBilling: {
    root: 'pendingBilling',
    list: 'list',
    getHash: (filter: PendingBillingFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  metrics: {
    root: 'metrics',
    billable: 'billable',
    callbacks: 'callbacks',
    avgTicket: 'avgTicket',
    revenue: 'revenue',
  },
  OCR: {
    root: 'OCR',
    list: 'OCR-list',
    getHash: (filter: OCRFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  transaction: {
    root: 'transaction',
    list: 'transaction-list',
    getTransactionHash: (req: TransactionQueryFilters) => JSON.stringify(sortObjectKeys(req)),
    getTransactionAdminHash: (
      data: Pick<Parameters<typeof applyFilters>[0], 'filters' | 'isSU' | 'departmentIDList'> & {
        page: number;
      },
    ) => JSON.stringify(sortObjectKeys(data)),
  },
  transactionLineItem: {
    root: 'transaction-line-item',
    list: 'transaction-line-item-list',
    getHash: (filter: TransactionLineItemFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  transactionDocument: {
    root: 'transaction-document',
    data: 'transaction-document-data',
    list: 'transaction-document-list',
    getFilterHash: (req: TransactionDocumentFilter) => JSON.stringify(sortObjectKeys(req)),
  },
  transactionGet: {
    root: 'transaction-get',
    getFiltersHash: (req: TransactionQueryFilters) => JSON.stringify(sortObjectKeys(req)),
  },
  transactionActivity: {
    root: 'transaction-activity',
    list: 'transaction-activity-list',
    getFiltersHash: (req: TransactionActivityFilter) => JSON.stringify(sortObjectKeys(req)),
  },
  transactionAccountBatch: {
    root: 'transaction-accountBatchGet',
  },
  transactionAccountGet: {
    root: 'transaction-account-get',
    getFiltersHash: (req: TransactionAccountFilters) => JSON.stringify(sortObjectKeys(req)),
  },
  transactionAccountBatchGet: {
    root: 'transaction-account-batch-get',
  },
  quoteLine: {
    root: 'quoteLine',
    list: 'quoteLine-list',
    getQuoteLineHash: (filters: QuoteLineFilters) => JSON.stringify(sortObjectKeys(filters)),
  },
  quotePhoto: {
    root: 'quotePhoto',
    list: 'quotePhoto-list',
    getQuotePhotoHash: (filters: QuotePhotoFilters) => JSON.stringify(sortObjectKeys(filters)),
  },
  vendor: {
    root: 'vendor',
    list: 'vendor-list',
    getVendorHash: (params: VendorQueryFilters) => JSON.stringify(sortObjectKeys(params)),
  },
  timesheetDepartments: {
    root: 'timesheetDepartments',
    list: 'timesheetDepartments-list',
    getFiltersHash: (params: TimesheetDepartmentFilters) => JSON.stringify(sortObjectKeys(params)),
    byIds: (ids: number[]) =>
      `ids-${ids
        .slice()
        .sort((a, b) => a - b)
        .join(',')}`,
  },
  timesheetDepartment: {
    root: 'timesheetDepartment',
    getFiltersHash: (params: TimesheetDepartmentFilters) => JSON.stringify(sortObjectKeys(params)),
  },
  serviceCalendar: {
    root: 'service-calendar',
    employees: 'service-calendar-employees',
    getServiceCalendarHash: (req: Event) => JSON.stringify(sortObjectKeys({ ...req })),
  },
  transactionStatus: {
    root: 'transactionStatus',
    list: 'transactionStatus-list',
    getHash: (filters: TransactionStatusFilters) => JSON.stringify(sortObjectKeys(filters)),
  },
  classcode: {
    root: 'classCode',
    list: 'classCode-list',
    getClassCodeHash: (filters: ClassCodeFilters) => JSON.stringify(sortObjectKeys(filters)),
  },
  timeOff: {
    root: 'timeOff',
    batchGet: 'timeOff-batchGet',

    requestTypes: 'timeOff-requestTypes',
    getTimeOffReqHash: (req: TimeOffFilter) => JSON.stringify(sortObjectKeys(req)),
  },
  timeOffRequestTypes: {
    root: 'timeOffRequestTypes',
    list: 'timeOffRequestTypes-list',
  },
  transactionAccount: {
    root: 'transactionAccount',
    getTransactionAccountHash: (filters: TransactionAccountFilters) =>
      JSON.stringify(sortObjectKeys(filters)),
  },
  fileTags: {
    root: 'fileTags',
    getFileTagsHash: (filter: FileTagsFilters) => JSON.stringify(sortObjectKeys(filter)),
  },
  trip: {
    root: 'trip',
    list: 'tripList',

    getHash: (filter: TripFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  perDiem: {
    root: 'perDiem',
    list: 'perDiemList',

    getPerDiemHash: (filter: PerDiemFilter) => JSON.stringify(sortObjectKeys(filter)),
    byDepartmentIdsAndDateStartedQuery: {
      key: 'perDiemByDepartmentIdsAndDateStartedQuery',
      hash: (
        ...args: Parameters<typeof PerDiemClientService.loadPerDiemByDepartmentIdsAndDateStarted>
      ) => JSON.stringify(sortObjectKeys({ depIds: args[0], dateStarted: args[1] })),
    },
  },
  apiKey: {
    root: 'apiKey',
    getApiKeyHash: (apiKey: ApiKeyFilter) => JSON.stringify(sortObjectKeys(apiKey)),
  },
  timeSheet: {
    root: 'timeSheet',
    list: 'timeSheetList',
    manager: 'timeSheetManager',
    payroll: 'timeSheetPayroll',
    getHash: (filter: TimesheetLineFilter) => JSON.stringify(sortObjectKeys(filter)),
    timeSheetCheck: 'timeSheetCheck',
    getTimeSheetHash: (filter: TimeSheetFilters) => JSON.stringify(sortObjectKeys(filter)),
  },
  reimbursement: {
    root: 'reimbursement',
    list: 'reimbursementList',

    getReimbursementHash: (filter: ReimbursementFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  reimbursementDocument: {
    root: 'reimbursementDocument',
    list: 'reimbursementDocumentList',
    getHash: (filter: ReimbursementDocumentFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  task: {
    root: 'task',
    list: 'taskList',

    getHash: (filter: TaskFilter) => JSON.stringify(sortObjectKeys(filter)),
    toolBudget: 'task-budget',
    appliedSpiffs: 'task-appliedSpiffs',
    spiffOptions: 'task-spiffOptions',
  },
  generatedTask: {
    root: 'generatedTask',
    list: 'generatedTaskList',
    getHash: (filter: GeneratedTaskFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  generatedTaskDocument: {
    root: 'generatedTaskDocument',
    list: 'generatedTaskDocumentList',
    getHash: (filter: GeneratedTaskDocumentFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  spiffTypes: {
    root: 'spiffTypes',
    list: 'spiffTypesList',
    getHash: (filter: SpiffTypeFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  spiffOptions: {
    root: 'spiffOptions',
    list: 'spiffOptionsList',
    getHash: (filter: SpiffOptionFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  documents: {
    root: 'documents',
    list: 'documentsList',
    getHash: (filter: DocumentFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  internalDocuments: {
    root: 'internalDocuments',
    list: 'internalDocumentsList',
    getHash: (filter: InternalDocumentFilter) => JSON.stringify(sortObjectKeys(filter)),
    getListHash: (
      filters: Parameters<typeof InternalDocumentClientService.loadInternalDocuments>[0],
    ) => JSON.stringify(sortObjectKeys(filters)),
  },
  internalResource: {
    root: 'internalResource',
    list: 'internalResourceList',
    infiniteList: 'internalResourceInfiniteList',
    getHash: (filter: LoadInternalResource) => JSON.stringify(sortObjectKeys(filter)),
  },
  creditCards: {
    root: 'creditCards',
    list: 'creditCardsList',
    getHash: (filter: CreditCardFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  geolocation: {
    root: 'geolocation',
    geocode: 'geocode',
    distanceMatrix: 'distanceMatrix',
    byAddress: 'loadGeoLocationByAddress',
    tripDistance: 'getTripDistance',
  },
  readings: {
    root: 'readings',
    list: 'readingsList',
    getHash: (filter: ReadingFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  serviceItem: {
    root: 'serviceItemClientService',
    list: 'serviceItemClientServiceList',
    search: 'serviceItemClientServiceSearch',
    getHash: (filter: ServiceFilter) => JSON.stringify(sortObjectKeys(filter)),
    get: (id: number) => ['serviceItemClientService', 'get', id],
  },
  serviceItemImage: {
    root: 'serviceItemClientService',
    list: 'serviceItemClientServiceList',
    getHash: (filter: ServiceFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  material: {
    root: 'material',
    list: 'materialList',
    getHash: (filter: ServiceFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  diagnosis: {
    root: 'diagnosis',
    list: 'diagnosisList',
    getHash: (filter: DiagnosisFilter) => JSON.stringify(sortObjectKeys(filter)),
    get: (id: number) => ['diagnosis', 'get', id],
  },
  diagnosisStatus: {
    root: 'diagnosisStatus',
    list: 'diagnosisStatusList',
    getHash: (filter: DiagnosisStatusFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  refrigerantType: {
    root: 'refrigerantType',
    list: 'refrigerantTypeList',
    getHash: (filter: RefrigerantTypeFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  systemStatus: {
    root: 'systemStatus',
    list: 'systemStatusList',
    getHash: (filter: SystemStatusFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  maintenanceQuestion: {
    root: 'maintenanceQuestion',
    list: 'maintenanceQuestionList',
    getHash: (filter: MaintenanceFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  projectsEvents: {
    root: 'projectEvents',
    list: 'list',
    getHash: (filter: ProjectEventFilter) => JSON.stringify(filter),
    financials: (projectEventId: number) => ['financials', projectEventId],
  },
  internalLink: {
    root: 'internalLink',
    list: 'InternalLinkList',
    getHash: (filter: InternalLinkFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  reportList: {
    root: 'report',
    list: 'reportList',
    getHash: (filter: ReportFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  commDiff: {
    root: 'commDiff',
    list: 'commDiffList',
    filter: (filter: { start: string; end: string }) => [
      'commDiff',
      'list',
      JSON.stringify(sortObjectKeys(filter)),
    ],
  },
  newHire: {
    root: 'newHire',
    list: 'newHireList',
    optionsList: 'newHireOptionsList',
    getHash: (filter: NewHireFilter | NewHireOptionFilter) =>
      JSON.stringify(sortObjectKeys(filter)),
  },
  projectEstimate: {
    project: 'project',
    projectList: 'projectList',
    estimate: 'estimate',
    changeOrder: 'changeOrder',
    expended: 'expended',
    expendedList: 'expendedList',
    relatedDepartmentBudgets: 'relatedDepartmentBudgets',
    getProjectHash: (jobNumber: number) => `project-${jobNumber}`,
    getEstimateHash: (jobNumber: number) => `estimate-${jobNumber}`,
    getChangeOrderHash: (jobNumber: number) => `changeOrder-${jobNumber}`,
    getExpendedHash: (jobNumber: number) => `expended-${jobNumber}`,
  },
  invoicePayment: {
    root: 'invoicePayment',
    list: 'invoicePaymentList',
    getHash: (filter: InvoiceFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  vehicles: {
    root: 'vehicles',
    list: 'vehiclesList',
    getHash: (filter: VehicleFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  vehiclePhoto: {
    root: 'vehiclePhoto',
    list: 'vehiclePhotoList',
    getHash: (filter: VehiclePhotoFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  vehicleTrailer: {
    root: 'vehicleTrailer',
    list: 'vehicleTrailerList',
    getHash: (filter: VehicleTrailerFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  vehicleAsset: {
    root: 'vehicleAsset',
    list: 'vehicleAssetList',
    getHash: (filter: VehicleAssetFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  vehicleLog: {
    root: 'vehicleLogs',
    list: 'vehicleLogsList',
    getHash: (filter: VehicleLogFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  aiAssistant: {
    root: 'aiAssistant',
    list: 'aiAssistantList',
  },
  aiResponse: {
    root: 'aiResponse',
    list: 'aiResponseList',
    getHash: (filter: AiResponseFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  aiResponseReport: {
    root: 'aiResponseReport',
  },
  aiChatPrompt: {
    root: 'aiChatPrompt',
    list: 'aiChatPromptList',
    getHash: (filter: AiChatPromptFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  employeeWeeklyReport: {
    root: 'employeeWeeklyReport',
  },
  kpiReport: {
    root: 'kpiReport',
    getHash: (filter: EmployeeKPIQueryFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  timeOffReport: {
    root: 'timeOffReport',
    getHash: (filter: TimeOffMetricRequestFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  toolFund: {
    root: 'toolFund',
    approvedTools: 'approvedTools',
    catalog: 'catalog',
    importStatus: 'importStatus',
    getCatalogHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
    getHash: (req: unknown) => JSON.stringify(req),
  },
  toolFundMetrics: {
    root: 'toolFundMetric',
  },
  toolFundPendingRequestCount: {
    root: 'toolFundPendingRequestCount',
  },
  tradeType: {
    root: 'tradeType',
    list: 'tradeTypeList',
    getHash: (filter: TradeTypeFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  toolFundSettings: {
    root: 'toolFundSettings',
    list: 'toolFundSettingsList',
    getHash: (filter: ToolFundSettingsFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  toolFundBalance: {
    root: 'toolFundBalance',
    getHash: (user: User) => JSON.stringify(sortObjectKeys({ userId: user.id })),
  },
  toolFundTransaction: {
    root: 'toolFundTransaction',
    list: 'toolFundTransactionList',
    transactionTypes: 'toolFundTransactionTypes',
    getHash: (filter: ToolFundTransactionFilter) => JSON.stringify(sortObjectKeys(filter)),
    getTransactionTypesHash: (filter: Record<string, unknown>) =>
      JSON.stringify(sortObjectKeys(filter)),
  },
  requisition: {
    root: 'requisition',
    list: 'requisition-list',
    details: (id: number) => ['requisition', id],
    getHash: (request: RequisitionFilter) => JSON.stringify(sortObjectKeys(request)),
    status: 'requisition-status',
  },
  catalogItems: {
    root: 'catalogItems',
    list: 'catalogItemsList',
    getHash: (request: CatalogItemFilter) => JSON.stringify(sortObjectKeys(request)),
  },
  catalogItemCategory: {
    root: 'catalogItemCategory',
    list: 'catalogItemCategoryList',
    getHash: (filter: CatalogItemCategoryFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  toolTradeMappings: {
    root: 'toolTradeMappings',
    list: 'toolTradeMappingsList',
    getHash: (filter: ToolTradeMappingFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  toolSet: {
    root: 'toolSet',
    list: 'toolSetList',
    details: (id: number) => ['toolSet', 'details', id],
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  activityLog: {
    root: 'activityLog',
    list: 'activityLogList',
    getHash: (filter: ActivityLogFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  servicesRendered: {
    root: 'servicesRendered',
    list: 'servicesRenderedList',
    getHash: (filter: ServicesRenderedFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  toolFundCatalogItemCategory: {
    root: 'toolFundCatalogItemCategory',
    list: 'toolFundCatalogItemCategoryList',
    getHash: (filter: CatalogItemCategoryFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  assetInstance: {
    root: 'assetInstance',
    list: 'assetInstanceList',
    getHash: (filter: AssetInstanceFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  performanceReviewStatus: {
    root: 'performanceReviewStatus',
    list: 'performanceReviewStatusList',
  },
  performanceReview: {
    root: 'performanceReview',
    list: 'performanceReviewList',
    forms: 'performanceReviewForms',
    payChanges: 'performanceReviewPayChanges',
    history: 'performanceReviewHistory',
    getHash: (filter: PerformanceReviewFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  files: {
    root: 'files',
    list: 'files-list',
    getHash: (filter: { id?: number }) => JSON.stringify(sortObjectKeys(filter)),
  },
  proposal: {
    root: 'proposal',
    getHash: (filter: GetRequest | TokenProposal) =>
      JSON.stringify(sortObjectKeys({ ...filter } as Record<string, unknown>)),
  },
  proposalIntakeForm: {
    root: 'proposalIntakeForm',
    list: 'proposalIntakeFormList',
    queueMove: 'proposalIntakeFormQueueMove',
    getHash: (filter: EntityFilter<ProposalIntakeForm>) => JSON.stringify(sortObjectKeys(filter)),
  },
  proposalIntakeSystem: {
    root: 'proposalIntakeSystem',
    list: 'proposalIntakeSystemList',
    getHash: (filter: EntityFilter<ProposalIntakeSystem>) => JSON.stringify(sortObjectKeys(filter)),
  },
  quoteDocument: {
    root: 'quoteDocument',
    list: 'quoteDocumentList',
    getQuoteDocumentHash: (filter: QuoteDocumentFilters) => JSON.stringify(sortObjectKeys(filter)),
  },
  sellSheet: {
    root: 'sellSheet',
    batchGet: 'sellSheet-batchGet',
    getHash: (filter: SellSheetFilters) => JSON.stringify(sortObjectKeys(filter)),
  },
  reports: {
    root: 'reports',
    timeoffAudit: 'timeoff-audit',
    systemInstallAnniversary: 'system-install-anniversary',
  },
  dispatch: {
    root: 'dispatch',
    dispatchableTechs: 'dispatchable-techs',
    getDispatchableTechsHash: (filter: DispatchableTechFilter) => JSON.stringify(filter),
    dispatchCalls: 'dispatch-calls',
    getDispatchCallsHash: (filter: DispatchCallFilter) => JSON.stringify(filter),
    firstCallSectors: 'first-call-sectors',
  },
  callWindow: {
    root: 'callWindow',
    list: 'callWindowList',
    getHash: (filter: CallWindowFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  emailTask: {
    root: 'emailTask',
    list: 'emailTaskList',
    recipients: 'emailTaskRecipients',
    scheduleTypes: 'emailTaskScheduleTypes',
  },
  callWindowJobCap: {
    root: 'callWindowJobCap',
    list: 'callWindowJobCapList',
    getHash: (filter: CallWindowJobCapFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  callWindowLimit: {
    root: 'callWindowLimit',
    list: 'callWindowLimitList',
    getHash: (filter: CallWindowLimitFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  callWindowLimitDate: {
    root: 'callWindowLimitDate',
    list: 'callWindowLimitDateList',
    getHash: (filter: CallWindowLimitDateFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  roleCategory: {
    root: 'roleCategory',
    list: 'roleCategoryList',
  },
  role: {
    root: 'role',
    list: 'roleList',
    getHash: (departmentId?: number) => `role-dept-${departmentId ?? 'all'}`,
  },
  roleDepartment: {
    root: 'roleDepartment',
    list: 'roleDepartmentList',
  },
  organizationGroup: {
    root: 'organizationGroup',
    list: 'organizationGroupList',
  },
  groupMember: {
    root: 'groupMember',
    list: 'groupMemberList',
    getHash: (filter: { userId?: number; organizationGroupId?: number }) =>
      `gm-u${filter.userId ?? 'any'}-g${filter.organizationGroupId ?? 'any'}`,
  },
  unityBudget: {
    root: 'unityBudget',
    contract: 'unityBudget-contract',
    contracts: 'unityBudget-contracts',
    getContractHash: (customId: string) => `unity-contract-${customId}`,
    getContractsHash: (ids: string[]) => `unity-contracts-${ids.sort().join('-')}`,
  },
  terminationForm: {
    root: 'terminationForm',
    get: 'terminationForm-get',
    list: 'terminationForm-list',
    create: 'terminationForm-create',
    edit: 'terminationForm-edit',
    getHash: (filter: TerminationFormFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  stripe: {
    root: 'stripe',
    eventData: 'stripe-event-data',
    eventDataList: 'stripe-event-data-list',
    customer: 'stripe-customer',
    customerList: 'stripe-customer-list',
    paymentMethods: 'stripe-payment-methods',
    subscription: 'stripe-subscription',
    subscriptionList: 'stripe-subscription-list',
    invoice: 'stripe-invoice',
    getEventDataHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
    getCustomerHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
    getPaymentMethodsHash: (filter: Record<string, unknown>) =>
      JSON.stringify(sortObjectKeys(filter)),
    getSubscriptionHash: (filter: Record<string, unknown>) =>
      JSON.stringify(sortObjectKeys(filter)),
  },
  inventoryCatalog: {
    root: 'inventoryCatalog',
    list: 'inventoryCatalogList',
    detail: 'inventoryCatalogDetail',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  eventPhoto: {
    root: 'eventPhoto',
    list: 'eventPhotoList',
    getHash: (filter: EventPhotoFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  formType: {
    root: 'formType',
    list: 'formTypeList',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  formStatus: {
    root: 'formStatus',
    list: 'formStatusList',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  projectCSIDivision: {
    root: 'projectCSIDivision',
    list: 'projectCSIDivisionList',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  projectCSISubtype: {
    root: 'projectCSISubtype',
    list: 'projectCSISubtypeList',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  formSubmission: {
    root: 'formSubmission',
    list: 'formSubmissionList',
    get: (id: number) => ['formSubmission', id],
    getHash: (filter: FormSubmissionFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  formSubmissionDocument: {
    root: 'formSubmissionDocument',
    list: 'formSubmissionDocumentList',
    getHash: (filter: Record<string, unknown>) => JSON.stringify(sortObjectKeys(filter)),
  },
  subcontractRequestDetail: {
    root: 'subcontractRequestDetail',
    get: (id: number) => ['subcontractRequestDetail', id],
    getByFormSubmissionId: (formSubmissionId: number) => [
      'subcontractRequestDetail',
      'formSubmission',
      formSubmissionId,
    ],
  },
  ntoRequestDetail: {
    root: 'ntoRequestDetail',
    get: (id: number) => ['ntoRequestDetail', id],
    getByFormSubmissionId: (formSubmissionId: number) => [
      'ntoRequestDetail',
      'formSubmission',
      formSubmissionId,
    ],
  },
  projectScopeDetail: {
    root: 'projectScopeDetail',
    get: (id: number) => ['projectScopeDetail', id],
    getByFormSubmissionId: (formSubmissionId: number) => [
      'projectScopeDetail',
      'formSubmission',
      formSubmissionId,
    ],
  },
  customerAccount: {
    root: 'customerAccount',
  },
  business: {
    root: 'business',
    list: 'business-list',
  },
  membershipCatalog: {
    root: 'membership-catalog',
    plans: 'membership-catalog-plans',
    addOns: 'membership-catalog-add-ons',
    eligibility: 'membership-catalog-eligibility',
    activeCount: 'membership-catalog-active-count',
  },
  qboItemMapping: {
    root: 'qbo-item-mappings',
    list: 'qbo-item-mappings',
    discover: 'qbo-items-discover',
  },
  qboCustomerMapping: {
    root: 'qbo-customer-mappings',
    list: 'qbo-customer-mappings-list',
    qboCustomers: 'qbo-customers-list',
  },
  qboClassMapping: {
    root: 'qbo-class-mappings',
    list: 'qbo-class-mappings-list',
    discover: 'qbo-classes-discover',
  },
  lmsUserCourses: {
    root: 'lmsUserCourses',
    byEmail: (email: string) => ['lmsUserCourses', email] as const,
  },
  measureQuickReadings: {
    root: 'measureQuickReadings',
    list: 'measureQuickReadingsList',
    getHash: (filter: MeasureQuickReadingsFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  measureQuickDispatches: {
    root: 'measureQuickDispatches',
    list: 'measureQuickDispatchesList',
    getHash: (filter: MeasureQuickDispatchesFilter) => JSON.stringify(sortObjectKeys(filter)),
  },
  serviceArea: {
    root: 'serviceArea',
    active: 'serviceAreaActive',
  },
} as const;
